/** Watermark gravada NO CANVAS (Fase 03 TASK-10, camada 1 de 2).
 *
 * É a camada que vale. Não há nó de DOM para remover pelo inspetor, ela
 * sobrevive a "salvar imagem" e acompanha o zoom de graça, porque é desenhada
 * na mesma escala da página. A camada de DOM (ver `Watermark.tsx`) existe só
 * para o caso de o navegador não dar contexto 2D.
 *
 * ⚠️ Continua sendo **best-effort** (ADR-009): captura de tela copia a marca
 * junto, e é por isso que ela leva o nome do aluno — a proteção não é impedir a
 * cópia, é tornar a cópia rastreável. Isso precisa estar escrito para o cliente.
 */

export interface OpcoesDaWatermark {
  texto: string
  /** Dimensões em CSS px da página desenhada. */
  largura: number
  altura: number
  /** Densidade do bitmap: o desenho é em CSS px e a transformação cuida do resto. */
  dpr?: number
  opacidade?: number
  /** Ângulo em graus; negativo inclina para cima, como marca de papel timbrado. */
  angulo?: number
}

const ESPACAMENTO_X = 320
const ESPACAMENTO_Y = 200

/** Desenha a marca ladrilhada sobre a página inteira.
 *
 * Ladrilhada e não uma marca central: recortar a imagem para fugir de uma marca
 * única é trivial, e um recorte que preserve texto legível sempre pega ao menos
 * uma repetição.
 */
export function desenharWatermark(
  contexto: CanvasRenderingContext2D,
  { texto, largura, altura, dpr = 1, opacidade = 0.08, angulo = -24 }: OpcoesDaWatermark,
): void {
  if (texto.trim() === '' || largura <= 0 || altura <= 0) return

  contexto.save()
  // Zera o que o pdf.js deixou e volta a coordenadas em CSS px — sem isto a
  // marca herdaria a transformação do render e sairia em escala errada.
  contexto.setTransform(dpr, 0, 0, dpr, 0, 0)
  contexto.globalAlpha = opacidade
  contexto.fillStyle = '#111827'
  contexto.font = '600 20px system-ui, sans-serif'
  contexto.textAlign = 'center'

  const radianos = (angulo * Math.PI) / 180
  // A diagonal cobre a página inclinada inteira; sem a folga, a rotação deixa
  // os cantos sem marca.
  const alcance = Math.ceil(Math.hypot(largura, altura))

  for (let y = -alcance; y < alcance; y += ESPACAMENTO_Y) {
    for (let x = -alcance; x < alcance; x += ESPACAMENTO_X) {
      contexto.save()
      contexto.translate(largura / 2 + x, altura / 2 + y)
      contexto.rotate(radianos)
      contexto.fillText(texto, 0, 0)
      contexto.restore()
    }
  }

  contexto.restore()
}

/** Texto da marca: quem está lendo e quando. */
export function textoDaWatermark(nomeDoAluno: string, data = new Date()): string {
  const nome = nomeDoAluno.trim()
  const quando = data.toLocaleDateString('pt-BR')
  return nome === '' ? quando : `${nome} · ${quando}`
}
